import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { createForm, useCreateFormStore } from '@components/form';
import { Header } from '@components/header';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useEditorState } from '@components/text-editor';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { isSameEditorState } from '@utils/draftjs';

import {
  useEditRewards,
  useEditSocial,
  useGetEmailTemplate,
  useGetJob,
  useGetRewards,
  useGetSocial,
  useUpdateEmailTemplate,
} from 'api/jobs/hooks';
import { EmailTemplateData, RewardsData, SocialData } from 'api/jobs/requests';
import { Loading } from 'components/Loading';
import { Job } from 'types/jobs';

import { convertFromEditorStateToHtml } from './settings/common';
import { EmailSettings } from './settings/email';
import { RewardsSettings } from './settings/rewards';
import { SocialSettings } from './settings/social';
import { Actions } from './top-bar-actions';
import { useUnsavedChangesPrompt } from './unsaved-changes-modal';

export type SocialFormValues = {
  sharingTitle: string;
  sharingDescription: string;
  sharingImage: File | null;
};
export type RewardsFormValues = {
  isRewardsEnabled: boolean;
  rewardsTitle: string | null;
  rewardsDescription: string | null;
};

export type EmailFormValues = {
  useCompanyEmail: boolean;
  emailSubject: string;
  emailBody: string;
};

export const Settings = () => {
  const { jobId } = useParams();

  const socialQuery = useGetSocial(Number(jobId));
  const rewardsQuery = useGetRewards(Number(jobId));
  const emailQuery = useGetEmailTemplate(Number(jobId));
  const jobQuery = useGetJob(Number(jobId));

  if (!socialQuery.data || !jobQuery.data || !rewardsQuery.data || !emailQuery.data)
    return <Loading />;
  return (
    <RenderSettings
      socialData={socialQuery.data}
      rewardsData={rewardsQuery.data}
      job={jobQuery.data}
      emailData={emailQuery.data}
    />
  );
};

const SocialForm = createForm<SocialFormValues>();
const RewardsForm = createForm<RewardsFormValues>();
const EmailForm = createForm<EmailFormValues>();

export const RenderSettings = ({
  socialData,
  rewardsData,
  job,
  emailData,
}: {
  socialData: SocialData;
  rewardsData: RewardsData;
  job: Job;
  emailData: EmailTemplateData;
}) => {
  const { t } = useScopedTranslate(SettingsLocale);
  const { jobId } = useParams();

  const saveSocial = useEditSocial();
  const saveRewards = useEditRewards();
  const saveEmail = useUpdateEmailTemplate();

  const socialStore = useCreateFormStore();
  const rewardsStore = useCreateFormStore();
  const emailStore = useCreateFormStore();

  const [sharingImage, setSharingImage] = useState<File | null>(null);
  const [sharingImageUrl, setSharingImageUrl] = useState<string | null>(null);

  const initialEmailHtml = emailData.jobTemplate?.contentHtml ?? emailData.defaultContent ?? '';
  const {
    editorState: emailEditorState,
    resetEditorState: resetEmailEditorState,
    isEmpty: isEmailEmpty,
    setEditorState: setEmailEditorState,
  } = useEditorState(initialEmailHtml);

  const isEmailEditorDirty = useMemo(
    () => !isSameEditorState(initialEmailHtml, convertFromEditorStateToHtml(emailEditorState)),
    [initialEmailHtml, emailEditorState]
  );

  const socialFormValues = socialStore.useFormValues();
  const rewardsFormValues = rewardsStore.useFormValues();
  const emailFormValues = emailStore.useFormValues();

  const isSocialFormDirty = socialStore.useIsDirty();
  const isEmailFormDirty = emailStore.useIsDirty();

  const isSocialDirty = sharingImageUrl !== null || isSocialFormDirty;
  const isRewardsDirty = rewardsStore.useIsDirty();
  const isEmailDirty = isEmailFormDirty || (isEmailEditorDirty && !emailFormValues.useCompanyEmail);
  const isDirty = isSocialDirty || isRewardsDirty || isEmailDirty;

  const { showSuccess, showError } = useToaster();

  useEffect(() => {
    if (sharingImage) {
      setSharingImageUrl(URL.createObjectURL(sharingImage));
    }
  }, [sharingImage]);

  useUnsavedChangesPrompt({
    isDirty: isDirty,
    onSave: async () => {
      await onSaveSocial();
      await onSaveRewards();
      await onSaveEmail();
    },
    isPublished: job.isPublished,
  });

  const onSaveSocial = async () => {
    if (!socialStore.actions.validateForm()) return;
    if (isSocialDirty) {
      const data = socialFormValues;
      const response = await saveSocial.mutateAsync({
        jobId: Number(jobId),
        image: sharingImage,
        title: data.sharingTitle as string,
        description: data.sharingDescription as string,
      });
      return response;
    }
  };

  const onSaveRewards = async () => {
    if (!rewardsStore.actions.validateForm()) return;
    if (isRewardsDirty) {
      const data = rewardsFormValues;
      const response = await saveRewards.mutateAsync({
        jobId: Number(jobId),
        hasRewards: data.isRewardsEnabled as boolean,
        rewardsTitle: data.rewardsTitle as string,
        rewards: data.rewardsDescription as string,
      });
      return response;
    }
  };

  const onSaveEmail = async () => {
    if (!emailStore.actions.validateForm()) return;
    if (!emailFormValues.useCompanyEmail && isEmailEmpty) return;
    if (isEmailDirty) {
      const data = emailFormValues;
      const response = await saveEmail.mutateAsync({
        jobId: Number(jobId),
        useCompanyEmailTemplate: data.useCompanyEmail as boolean,
        contentHtml: convertFromEditorStateToHtml(emailEditorState),
      });
      return response;
    }
  };

  const onDiscardChanges = () => {
    setSharingImage(null);
    setSharingImageUrl(null);
    socialStore.actions.resetStore();
    rewardsStore.actions.resetStore();
    emailStore.actions.resetStore();
    resetEmailEditorState();
  };

  const onSaveSettings = async () => {
    if (!socialStore.actions.validateForm()) return;
    if (!rewardsStore.actions.validateForm()) return;
    if (!emailStore.actions.validateForm()) return;
    if (!emailFormValues.useCompanyEmail && isEmailEmpty) return;

    const saveSocialResponse = await onSaveSocial();
    const saveRewardsResponse = await onSaveRewards();
    const saveEmailResponse = await onSaveEmail();
    if (
      saveSocialResponse?.error === true ||
      saveRewardsResponse?.error === true ||
      saveEmailResponse?.error === true
    ) {
      showError(t('settingsUpdateError'));
    } else {
      showSuccess(t('settingsUpdated'));
      setSharingImageUrl(null);
    }
  };

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: job.title }]} />}
        actions={
          <Actions
            hasUnsavedChanges={isDirty}
            onSave={onSaveSettings}
            onCancel={onDiscardChanges}
            job={job}
            dataCyPublish="publish-job"
            dataCySave="save-button"
          />
        }
      />
      <PageContent>
        <MiddleColumnPage>
          <Stack space={6}>
            <Stack orientation="horizontal" space={4} align="center" justify="space-between">
              <Text font="heading2" text={t('settings')} />
            </Stack>
            <EmailForm store={emailStore} onSubmit={onSaveEmail}>
              <EmailSettings
                form={EmailForm}
                store={emailStore}
                emailEditorState={emailEditorState}
                isEmailEmpty={isEmailEmpty}
                setEmailEditorState={setEmailEditorState}
                initialData={emailData}
              />
            </EmailForm>
            <SocialForm store={socialStore} onSubmit={onSaveSocial}>
              <SocialSettings
                form={SocialForm}
                initialData={socialData}
                sharingImage={sharingImage}
                setSharingImage={setSharingImage}
              />
            </SocialForm>
            <RewardsForm store={rewardsStore} onSubmit={onSaveRewards}>
              <RewardsSettings form={RewardsForm} store={rewardsStore} initialData={rewardsData} />
            </RewardsForm>
          </Stack>
        </MiddleColumnPage>
      </PageContent>
    </>
  );
};

export const SettingsLocale = {
  en: {
    settings: 'Settings',
    socialImage: 'Social sharing image',
    settingsDescription:
      'When this ad is shared on social media, the default social sharing image is used.\nAdd an image here if you wish to use a custom image for this job.\n\nFor best results, use a landscape image: 1200 by 630 pixels.\nWe will shrink the width to 1200 if the image is wider.',
    socialSharingDetails: 'Social sharing details',
    socialSharingDetailsDescription:
      'Default title and description text that appears when you share a job on social platforms',
    sharingTitle: 'Sharing title',
    sharingDescription: 'Sharing description',
    rewards: 'Rewards',
    rewardDescription: 'Rewards if employee job share or sourcing leads to a hire',
    sourcingRewards: 'Sourcing bonus',
    disabled: 'Disabled',
    enabled: 'Enabled',
    yes: 'Yes',
    no: 'No',
    rewardTitle: 'Reward title',
    amountOrReward: 'Amount or reward',
    acceptedFileTypes: 'Accepted file types, .jpg, .png and .gif',
    replaceText: 'Add custom image',
    savedSuccess: 'Job settings saved',
    requiredField: 'This field is required',
    settingsUpdated: 'Settings updated',
    settingsUpdateError: 'Could not update settings',
    applicationEmailResponse: 'Application email response',
    applicationEmailResponseInfo:
      'Customize the automatic email applicants get when they submit their application.',
    useCompanyDefault: 'Use company default',
    emailTemplateTitle: 'Email template title',
    subject: 'Subject',
    content: 'Content',
    previewEmail: 'Preview email',
    placeholder: 'Placeholder',
    emailInfoText:
      'Customize the automatic email applicants get when they submit their application.',
    emailSubjectInfoText:
      'Email subject is automatically generated with applicant and job id and can not be edited',
  },
  is: {
    settings: 'Stillingar',
    socialImage: 'Sjálfgefin mynd fyrir samfélagsmiðla',
    settingsDescription:
      'Þegar starf er auglýst á samfélagsmiðlum er notast við þessa mynd, nema henni sé breytt á stöku starfi\n\nFyrir bestu niðurstöður notist við landscape mynd: 1200 x 630 pixla. Við aðlögum myndina niður í 1200 ef myndir er breiðari.',
    socialSharingDetails: 'Sjálfgefnar upplýsingar fyrir samfélagsmiðla',
    socialSharingDetailsDescription:
      'Sjálfgefinn titill og lýsingartexti sem birtist þegar þú deilir starfi á samfélagsmiðlum',
    sharingTitle: 'Titill á deilingu',
    sharingDescription: 'Lýsing á deilingu',
    rewards: 'Verðlaun',
    rewardDescription:
      'Bættu við möguleika á verðlaunum fyrir þessa starfsauglýsingu. Verðlaun geta haft hvetjandi áhrif á starfsmenn til að finna nýja umsækjendur.',
    sourcingRewards: 'Verðlaun fyrir ráðningu',
    disabled: 'Óvirk',
    enabled: 'Virkt',
    yes: 'Já',
    no: 'Nei',
    rewardTitle: 'Titill verðlauna',
    amountOrReward: 'Nánari lýsing á verðlaunum',
    acceptedFileTypes: 'Leyfilegar skráartegundir: .jpg, .png og .gif',
    replaceText: 'Breyta mynd',
    savedSuccess: 'Stillingar starfs vistaðar',
    requiredField: 'Nauðsynlegt er að fylla þennan reit út',
    settingsUpdated: 'Stillingar uppfærðar',
    settingsUpdateError: 'Ekki tókst að uppfæra stillingar',
    applicationEmailResponse: 'Svarpóstur umsóknar',
    applicationEmailResponseInfo:
      'Hér getur þú breytt textanum í tölvupóstinum sem umsækjendur fá við innsendingu umsóknar.',
    useCompanyDefault: 'Nota sjálfgefið fyrirtækjasniðmát',
    emailTemplateTitle: 'Heiti sniðmáts í notkun',
    subject: 'Efnislína (subject)',
    content: 'Innihald tölvupósts',
    previewEmail: 'Forskoða póst',
    placeholder: 'Staðsetningartexti',
    emailInfoText:
      'Hér getur þú breytt textanum í tölvupóstinum sem umsækjendur fá við innsendingu umsóknar.',
    emailSubjectInfoText:
      'Viðfang er sjálfkrafa búið til með aukenni umsækjenda og starfs og ekki er hægt að breyta',
  },
};
